import { useEffect, useRef } from "react";

/**
 * Video element that only plays while `shown` is true.
 * When shown becomes true: starts from beginning and plays.
 * When `maxPlays` is set: plays at most that many times then stops.
 */
export default function LoopVideo({ src, maxPlays, shown = true, onComplete, ...props }) {
  const ref = useRef(null);
  const playsRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Handle play/pause based on `shown`
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (shown) {
      playsRef.current = 0;
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      el.pause();
    }
  }, [shown]);

  // Handle ended → maxPlays logic; fire onComplete when target reached
  useEffect(() => {
    const el = ref.current;
    if (!el || !maxPlays) return;
    const onEnded = () => {
      playsRef.current += 1;
      if (playsRef.current < maxPlays) {
        el.currentTime = 0;
        const p = el.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } else if (onCompleteRef.current) {
        onCompleteRef.current();
      }
    };
    el.addEventListener("ended", onEnded);
    return () => el.removeEventListener("ended", onEnded);
  }, [maxPlays]);

  return (
    <video
      ref={ref}
      src={src}
      autoPlay
      muted
      playsInline
      preload="auto"
      loop={!maxPlays}
      {...props}
    />
  );
}
